/**
 * 529 Projection Engine — pure monthly step computation.
 *
 * Runs month-by-month from today to graduation, applying:
 *   - Monthly growth = (1 + annualRate)^(1/12) - 1
 *   - Monthly contribution = annualContribution / 12
 *   - Monthly education cost = annualCost / 12 (0 during pauses)
 *
 * Pauses: periods where no education cost is incurred but contributions
 * and growth continue. Graduation shifts out by the total pause months.
 *
 * @param {Object} params
 * @param {number} params.currentBalance - Starting 529 balance
 * @param {number} params.monthsRemaining - Months of education remaining (excluding pauses)
 * @param {number} params.annualCost - Expected annual education cost
 * @param {number} params.annualContribution - Annual future contribution
 * @param {number} params.annualGrowthRate - Expected annual growth rate (e.g., 0.03 for 3%)
 * @param {number} params.annualCostInflationRate - Expected annual education cost inflation (e.g., 0.03 for 3%)
 * @param {Array}  params.pauses - Array of { startMonth: number, durationMonths: number }
 *                                  startMonth is 1-indexed from the first projection month
 * @returns {Object} { months, yearSummary, verdict, shortfallMonth, endBalance, totalMonths }
 */
export function runProjection({
  currentBalance = 0,
  monthsRemaining = null,
  annualCost = 0,
  annualContribution = 0,
  annualGrowthRate = 0.03,
  annualCostInflationRate = 0.03,
  pauses = [],
}) {
  // No end date at all — cannot project
  if (monthsRemaining == null) {
    return {
      months: [],
      yearSummary: [],
      verdict: "no_data",
      shortfallMonth: null,
      endBalance: currentBalance,
      totalMonths: 0,
    };
  }

  // End date is in the past or current month — enrollment complete
  if (monthsRemaining <= 0) {
    return {
      months: [],
      yearSummary: [],
      verdict: "complete",
      shortfallMonth: null,
      endBalance: currentBalance,
      totalMonths: 0,
    };
  }

  const monthlyGrowthRate = Math.pow(1 + annualGrowthRate, 1 / 12) - 1;
  const monthlyContribution = annualContribution / 12;
  // Base monthly cost (year 0); inflated per projection-year below
  const baseMonthlyCost = annualCost / 12;

  // Build a set of paused month indices (1-indexed project months)
  const pausedMonths = new Set();
  let totalPauseMonths = 0;
  for (const p of pauses) {
    const start = p.startMonth || 1;
    const dur = p.durationMonths || 0;
    for (let i = 0; i < dur; i++) {
      pausedMonths.add(start + i);
    }
    totalPauseMonths += dur;
  }

  const totalMonths = monthsRemaining + totalPauseMonths;
  const now = new Date();
  let startYear = now.getFullYear();
  let startMonth = now.getMonth(); // 0-indexed

  let balance = currentBalance;
  const months = [];
  let shortfallMonth = null;
  let educationMonthsConsumed = 0;

  for (let m = 1; m <= totalMonths; m++) {
    const calMonth = (startMonth + m - 1) % 12; // 0-indexed month of year
    const calYear = startYear + Math.floor((startMonth + m - 1) / 12);
    const isPaused = pausedMonths.has(m);
    const growth = Math.round(balance * monthlyGrowthRate * 100) / 100;
    const contrib = Math.round(monthlyContribution * 100) / 100;
    // Inflation: full projection-years elapsed since start (0-indexed)
    const yearsElapsed = Math.floor((m - 1) / 12);
    const inflatedMonthlyCost = baseMonthlyCost * Math.pow(1 + annualCostInflationRate, yearsElapsed);
    const cost = isPaused ? 0 : Math.round(inflatedMonthlyCost * 100) / 100;

    if (!isPaused) {
      educationMonthsConsumed++;
    }

    const startBal = Math.round(balance * 100) / 100;
    balance = balance + growth + contrib - cost;
    const endBal = Math.round(balance * 100) / 100;

    months.push({
      month: m,
      calYear,
      calMonth: calMonth + 1, // 1-indexed
      startBalance: startBal,
      growth,
      contribution: contrib,
      cost,
      endBalance: endBal,
      isPaused,
    });

    if (endBal < 0 && !shortfallMonth) {
      shortfallMonth = {
        month: m,
        calYear,
        calMonth: calMonth + 1,
        label: `${["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][calMonth]} ${calYear}`,
        shortfall: Math.abs(endBal),
      };
    }
  }

  // Group by calendar year
  const yearMap = new Map();
  for (const m of months) {
    if (!yearMap.has(m.calYear)) {
      yearMap.set(m.calYear, {
        year: m.calYear,
        startBalance: m.startBalance,
        contributions: 0,
        growth: 0,
        cost: 0,
        endBalance: 0,
        hasPause: false,
      });
    }
    const yr = yearMap.get(m.calYear);
    yr.contributions += m.contribution;
    yr.growth += m.growth;
    yr.cost += m.cost;
    yr.endBalance = m.endBalance;
    if (m.isPaused) yr.hasPause = true;
  }
  const yearSummary = [...yearMap.values()].map((y) => ({
    ...y,
    contributions: Math.round(y.contributions * 100) / 100,
    growth: Math.round(y.growth * 100) / 100,
    cost: Math.round(y.cost * 100) / 100,
    startBalance: Math.round(y.startBalance * 100) / 100,
    endBalance: Math.round(y.endBalance * 100) / 100,
  }));

  const endBalance = Math.round(balance * 100) / 100;

  // Determine verdict
  let verdict;
  if (shortfallMonth) {
    verdict = "shortfall";
  } else if (endBalance < annualCost * 0.25 && endBalance >= 0) {
    verdict = "tight";
  } else {
    verdict = "surplus";
  }

  return {
    months,
    yearSummary,
    verdict,
    shortfallMonth,
    endBalance,
    totalMonths,
    graduationMonth: months.length > 0 ? months[months.length - 1] : null,
  };
}
